'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const { getQuoteMediaImgSrc } = require('../../lib/quoteMediaUtil.js');
const { breakpoints } = require('../../lib/constants');

const SIZES = [
    {
        width: '310',
        height: '250',
        mediaMinWidth: breakpoints.C,
    },
    {
        width: '800',
        height: '400',
        mediaMinWidth: breakpoints.B,
    },
    {
        width: '320',
        height: '300',
        mediaMinWidth: breakpoints.A,
    },
    {
        width: '320',
        height: '300',
        mediaMinWidth: breakpoints.A,
    },
];
const INDICES = {
    '^dji': 'Dow Jones Industrial Average',
    '^in': 'S&P 500',
    '^compx': 'NASDAQ Composite',
};
const SYMBOLS = Object.keys(INDICES);

getDependencyClass('phoenix-page').then((pageClass) => {
    class PhoenixQuoteMediaIndex extends pageClass.PhoenixElement {
        connectedCallback() {
            super.connectedCallback();
            this._qModEnv = this.config?.env || '';
            if (!this._qModEnv) {
                // eslint-disable-next-line no-console
                console.error(`QuoteMediaIndex: Missing QMod 'env' variable; rendering 'uat' data.`);
                this._qModEnv = 'uat';
            }
            this.baseUrl = this.config?.baseUrl ?? '/';
            this._render();
            const quoteMediaIndex = this;

            this.querySelector('.m-quote-media-index--miniquotes')?.addEventListener(
                'qmod-after-template-bind',
                function () {
                    const miniQuotesQMod = this;
                    const qModDateTime = miniQuotesQMod.querySelector('.qmod-datetime')?.innerText || '';
                    const datetime = quoteMediaIndex.querySelector('#m-quote-media-index--datetime');

                    if (!datetime) {
                        return;
                    }

                    datetime.innerText = qModDateTime;
                }
            );
        }

        _getSources(symbol) {
            return SIZES.map(({ width, height, mediaMinWidth }) => {
                const srcsetAttribute = getQuoteMediaImgSrc(symbol, width, height, this._qModEnv);
                const mediaAttribute = `(min-width: ${mediaMinWidth}px)`;

                return {
                    srcset: srcsetAttribute,
                    media: mediaAttribute,
                };
            });
        }

        _render() {
            const charts = SYMBOLS.map((symbol) => {
                return {
                    symbol: symbol,
                    longName: INDICES[symbol],
                    sources: this._getSources(symbol),
                    img: getQuoteMediaImgSrc(symbol, '320', '300', this._qModEnv),
                    miniQuotesParameters: {
                        symbol: symbol.toUpperCase(),
                    },
                    urlSymbol: symbol.toUpperCase(),
                };
            });

            this.innerHTML = `
                ${this._headerTemplate()}
                <phoenix-tabs class='m-tabs m-quote-media-index--container'>
                    ${this._indexTabsTemplate(charts)}
                    ${this._adTemplate()}
                    ${this._chartContainerTemplate(charts)}
                    ${this._dateTimeTemplate()}
                </phoenix-tabs>
            `;
        }

        _headerTemplate() {
            return `
                <div class='m-quote-media-index--header'>
                    <h1 class='m-quote-media-index--header-text'>Stock Market Today</h1>
                    <h2>Today's stock market data for DJIA, S&P 500, and NASDAQ</h2>
                </div>
            `;
        }

        _indexTabsTemplate(charts) {
            const loaderTemplate = () => {
                return `
                    <div class="m-quote-media-index--loading m-quote-media-index--w-20"></div>
                    <div class="m-quote-media-index--loading m-quote-media-index--w-50"></div>
                    <div class="m-quote-media-index--loading m-quote-media-index--w-80"></div>
                `;
            };

            return `
                <div class="m-quote-media-index--tabs">
                    ${charts
                        .map((chart, index) => {
                            return `
                                <phoenix-tab
                                    class="m-quote-media-index--tab"
                                    for='${chart.symbol}'
                                    tabindex="${index === 0 ? '0' : '-1'}"
                                >
                                    <div

                                        data-symbol="${chart.symbol}"
                                        data-longname="${chart.longName}"
                                        class="m-quote-media-index--symbol"
                                    >

                                        <div>
                                            <div
                                                class='qtool m-quote-media-index--miniquotes'
                                                data-qmod-tool='miniquotes'
                                                data-qmod-params=${JSON.stringify(chart.miniQuotesParameters)}
                                            >
                                                ${loaderTemplate()}
                                            </div>
                                        </div>
                                    </div>

                                </phoenix-tab>
                            `;
                        })
                        .join('')}
                </div>
            `;
        }

        _searchBoxTemplate() {
            return `
                <div class='m-quote-media-index--symbol-lookup-container'>
                    <phoenix-quote-media-symbol-lookup
                        class='m-quote-media-symbol-lookup m-quote-media-index--symbol-lookup'
                        config=${JSON.stringify({ baseUrl: this.baseUrl })}
                    />
                </div>
            `;
        }

        _adTemplate() {
            const ad = this.config?.ad;
            if (!ad) {
                return ``;
            }

            return `
                <phoenix-ad
                    config='${JSON.stringify(ad)}'
                    class='m-ad ${ad.visClasses} ${ad.moduleClass}--slot'
                ></phoenix-ad>
            `;
        }

        _chartContainerTemplate(charts) {
            return `
                <div class='m-quote-media-index--content'>
                    <div class='m-quote-media-index--panel-container'>
                        ${charts.map((chart, index) => this._chartSourceTemplate(chart, index)).join('')}
                    </div>
                </div>
            `;
        }

        _chartSourceTemplate(chart, index) {
            const sourceTemplates = () => {
                return `${chart.sources
                    .map((source) => `<source srcset='${source.srcset}' media='${source.media}'>`)
                    .join('')}
                `;
            };
            const href = `${this.baseUrl}quote/${chart.urlSymbol}`;
            const rel = this.baseUrl === '/' ? '' : `rel='noreferrer'`;
            const target = this.baseUrl === '/' ? '' : `target='_blank'`;

            return `
                <phoenix-tab-panel
                    id='${chart.symbol}'
                    class='m-quote-media-index--chart-wrapper'
                    ${index ? 'hidden' : ''}
                >
                    <span class='m-quote-media-index--long-name'>${chart.longName}</span>
                    <a class='m-quote-media-index--link' href='${href}' ${rel} ${target}>
                        <phoenix-picture>
                            <picture class='m-quote-media-index--chart'>
                                ${sourceTemplates()}
                                <img class='m-quote-media-index--chart-img' src='${chart.img}'>
                            </picture>
                        </phoenix-picture>
                    </a>
                </phoenix-tab-panel>
            `;
        }

        _dateTimeTemplate() {
            return `
                <p class='updated m-quote-media-index--time'>
                    Market Data updated <span id='m-quote-media-index--datetime'></span></br>
                    All prices delayed by 15 minutes.
                </p>
            `;
        }
    }

    window.customElements.define('phoenix-quote-media-index', PhoenixQuoteMediaIndex);
});
